import { ReactElement, forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import PhoneNumberInput, { PhoneNumberInputProps } from '../PhoneNumberInput';
import {
  ButtonTypes,
  FeedbackLabelInterface,
  LoginResponseInterface,
  OneTimePasswordInterface,
} from '../../services/exports/Interfaces';
import { Trans, useTranslation } from 'react-i18next';
import OtpInput from 'react-otp-input';
import CustomButton from '../buttons/CustomButton';
import Countdown, { zeroPad } from 'react-countdown';
import moment from 'moment/moment';
import ChatIcon from '../../assets/logo/ChatIcon';
import { ENVIRONMENTS, OTP_DELIVERY_CHANNELS } from '../../services/exports/Constants';
import AuthorizationManager, { LoginData, OtpLoginData } from '../../services/auth/AuthorizationManager';
import toast from 'react-hot-toast';
import { useSelector } from 'react-redux';
import { StoreInterface } from '../../store/types';
import useOrderFlow from '../../hooks/order/useOrderFlow';
import { DataWrappedResponse, Response } from '../../services/exports/Types';
import FeedbackLabel from '../labels/FeedbackLabel';
import useAuth from '../../hooks/global/useAuth';
import Turnstile from 'react-turnstile';
import useTheme from '../../hooks/ui/useTheme';
import useStorefront from '../../hooks/storefront/useStorefront';
import StorefrontTurnStile from '../../modules/storefront/components/widgets/TurnStile';

export type OtpFlowHandle = {
  requestCode: (delivery_channel?: OTP_DELIVERY_CHANNELS) => Promise<DataWrappedResponse<OneTimePasswordInterface>>;
  logIn: (data?: Partial<LoginData>) => Promise<Response<LoginResponseInterface>>;
};

interface Props {
  isStatic?: boolean;
  initialPhoneNumber?: string;
  extra?: ReactElement;
  onPhoneNumberChange?: (value: string) => void;
  onPasswordChange?: (value: string) => void;
  onCodeRequested?: (value?: string) => void;
  onValidationStatusChange?: (result: boolean) => void;
  className?: string;
  phoneNumberLabel?: ReactElement;
  phoneNumberInputProps?: Partial<PhoneNumberInputProps>;
}

const OTP_LENGTH = 4;

export const TURNSTILE_ENABLED = import.meta.env.PROD;
export const TURNSTILE_VERIFICATION_TOKEN_HEADER = 'x-turnstile-token';

const OtpFlow = forwardRef<OtpFlowHandle, Props>((props: Props, ref: any) => {
  useImperativeHandle(ref, () => ({ requestCode, logIn }));

  const { t, i18n } = useTranslation(null, { keyPrefix: 'Components:Auth:OtpFlow' });

  const {
    isStatic,
    initialPhoneNumber,
    extra,
    onPhoneNumberChange,
    onPasswordChange,
    onCodeRequested,
    onValidationStatusChange,
    className,
    phoneNumberLabel,
    phoneNumberInputProps,
  } = props;

  const { company } = useSelector((state: StoreInterface) => state.initialData);
  const { profile } = useSelector((state: StoreInterface) => state.profile);

  const { login } = useAuth();
  const { localData } = useOrderFlow();
  const theme = useTheme();
  const otpRef = useRef(null);
  const { isStorefront } = useStorefront();

  const [phoneNumber, setPhoneNumber] = useState(
    initialPhoneNumber ?? profile?.phone_number ?? localData.customer_phone_number,
  );
  const [country, setCountry] = useState(null);
  const [code, setCode] = useState(null);
  const [otp, setOtp] = useState<OneTimePasswordInterface>(null);
  const [turnstileToken, setTurnstileToken] = useState(null);

  const [hasEmailDeliveryChannel, setHasEmailDeliveryChannel] = useState(false);
  const [codeRequested, setCodeRequested] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendingViaChannel, setResendingViaChannel] = useState(null);
  const [retrySmsAfter, setRetrySmsAfter] = useState(null);
  const [feedback, setFeedback] = useState<FeedbackLabelInterface>({
    message: '',
  });

  useEffect(() => {
    isStatic &&
      (!TURNSTILE_ENABLED || (!codeRequested && Boolean(turnstileToken))) &&
      requestCode(OTP_DELIVERY_CHANNELS.SMS);
  }, [turnstileToken]);

  useEffect(() => {
    onPhoneNumberChange && onPhoneNumberChange(phoneNumber);
  }, [phoneNumber]);

  useEffect(() => {
    onPasswordChange && onPasswordChange(code);
  }, [code]);

  useEffect(() => {
    if (codeRequested) {
      scrollToOTP();
    }
    setRetrySmsAfter(60);
  }, [codeRequested]);

  useEffect(() => {
    onValidationStatusChange && onValidationStatusChange(validateInput());
  }, [codeRequested, code, phoneNumber, turnstileToken]);

  const scrollToOTP = () => {
    otpRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  async function requestCode(
    delivery_channel: OTP_DELIVERY_CHANNELS = undefined,
  ): Promise<DataWrappedResponse<OneTimePasswordInterface>> {
    setResendingViaChannel(delivery_channel);
    setResending(true);

    const request = await AuthorizationManager.postOptCode(
      {
        company_id: company?.id,
        phone_number: phoneNumber,
        lang: i18n.language,
        delivery_channel,
        country,
      },
      { [TURNSTILE_VERIFICATION_TOKEN_HEADER]: turnstileToken },
    );

    const { response, success } = request;
    setResending(false);

    if (success) {
      setCodeRequested(true);
      setOtp(response.data.data);
      !hasEmailDeliveryChannel &&
        setHasEmailDeliveryChannel(response.data.data.delivery_channel === OTP_DELIVERY_CHANNELS.EMAIL);
      response?.headers['retry-after'] && setRetrySmsAfter(response?.headers['retry-after']);

      if (import.meta.env.VITE_MODE !== ENVIRONMENTS.PROD) {
        setCode(response?.data?.data?.password);
      }

      resetErrorMessage();
      import.meta.env.PROD && toast.success(t('toasts.code_sent'));
      setTimeout(() => onCodeRequested && onCodeRequested(phoneNumber));
      scrollToOTP();

      return request;
    }

    requestCodeErrorHandler(response);

    return request;
  }

  async function logIn(data: Partial<OtpLoginData> = {}): Promise<Response<LoginResponseInterface>> {
    return login({
      company_id: company?.id,
      phone_number: phoneNumber,
      password: code,
      ...data,
    });
  }

  function requestCodeErrorHandler(error: any) {
    const status = error?.response?.status;
    switch (status) {
      case 429:
        setRetrySmsAfter(error?.response?.headers['retry-after'] ?? null);
        setFeedback({
          type: 'Error',
          message: t('error_messages.frequent_requests'),
        });
        break;
      default:
        setFeedback({
          type: 'Error',
          message: t('error_messages.invalid_number'),
        });
        break;
    }

    setTimeout(() => onCodeRequested && onCodeRequested());
  }

  function validateInput(): boolean {
    if (!codeRequested) {
      return phoneNumber?.length >= 10 && (!TURNSTILE_ENABLED || Boolean(turnstileToken));
    }

    return phoneNumber.length >= 10 && code?.length >= OTP_LENGTH;
  }

  function resetErrorMessage() {
    setFeedback({
      message: '',
    });
  }

  function renderResendOptions() {
    return (
      <div>
        <h6 className="mt-md text-sm">{t(`otp.resend.options.${otp?.delivery_channel}.title`)}</h6>
        <CustomButton
          title={
            <div className="text-black dark:text-white flex items-center">
              {retrySmsAfter > 0 ? (
                <Countdown
                  date={moment().add(retrySmsAfter, 'seconds').toDate()}
                  renderer={({ minutes, seconds }) => (
                    <span className="mr-xs my-auto">
                      ({zeroPad(minutes)}:{zeroPad(seconds)})
                    </span>
                  )}
                  onTick={() => setRetrySmsAfter((current) => current - 1)}
                  onComplete={() => setRetrySmsAfter(null)}
                />
              ) : null}
              <ChatIcon
                className="h-6 w-6 mr-xs my-auto"
                color={theme.isDarkMode ? theme.color.white.DEFAULT : theme.color.black.DEFAULT}
              />
              {t(`otp.resend.options.${OTP_DELIVERY_CHANNELS.SMS}.button`)}
            </div>
          }
          onClick={() => requestCode(OTP_DELIVERY_CHANNELS.SMS)}
          buttonType={ButtonTypes.sixth}
          className="mt-md !w-fit items-center"
          titleClass="flex"
          disabled={!!retrySmsAfter}
          loading={resending && resendingViaChannel === OTP_DELIVERY_CHANNELS.SMS}
        />
      </div>
    );
  }

  const turnstile = useMemo(() => {
    if (!TURNSTILE_ENABLED) {
      return null;
    }
    if (isStorefront) {
      return <StorefrontTurnStile onVerify={setTurnstileToken} />;
    }
    return (
      <Turnstile
        sitekey={import.meta.env.VITE_TURNSTILE_SITEKEY}
        onVerify={(token) => setTurnstileToken(token)}
        language={i18n.language}
        className="mt-md"
        theme="light"
      />
    );
  }, [isStorefront, i18n.language]);

  return (
    <div className={className}>
      {Boolean(feedback?.message) && <FeedbackLabel message={feedback?.message} className="mb-md" type="Error" />}
      <label htmlFor="phone_number">{phoneNumberLabel ?? t('form.phone_number.label')}</label>
      <PhoneNumberInput
        placeholder={t('form.phone_number.placeholder')}
        value={phoneNumber}
        onChange={setPhoneNumber}
        onChangeCountry={setCountry}
        initialCountry={country}
        disabled={isStatic}
        id="phone_number"
        className="mt-xs"
        {...phoneNumberInputProps}
      />
      {turnstile}
      {codeRequested && (
        <div className="mt-md">
          <div className="mt-md">
            {otp && (
              <div className="mb-md">
                <p className="text-sm">
                  <Trans
                    values={{
                      address: otp.delivery_address,
                    }}
                    t={t}
                  >
                    otp.text.{otp.delivery_channel}
                  </Trans>
                </p>
                <p className="text-xs mt-md">{t(`otp.tip.${otp.delivery_channel}`)}</p>
              </div>
            )}
            <div ref={otpRef}>
              <OtpInput
                value={code}
                onChange={(value) => setCode(value)}
                numInputs={4}
                shouldAutoFocus
                renderInput={(props) => (
                  <input
                    {...props}
                    inputMode="numeric"
                    className="bg-gray-400 dark:bg-black-600 mr-xs h-10 !w-10 rounded-md dark:border dark:!border-brand-border"
                  />
                )}
              />
            </div>
            {renderResendOptions()}
            {extra}
          </div>
        </div>
      )}
    </div>
  );
});

export default OtpFlow;
